import { GriffelStyle } from '@fluentui/react-components'

/**
 * Word's multilevel list markers (#6476).
 *
 * MIRRORED FILE. An identical copy lives in 365-robust-web at
 * src/components/common/rtf/rtfListStyles.ts. A change here has to be made there too,
 * or the author's editor and the reader's view drift apart.
 *
 * Word cycles its nine outline levels with a period of three — 1. a. i. 1. a. i. 1. a. i.
 * for numbers and • o ▪ for bullets — and the level is the *visual* nesting depth, not
 * the number of ancestors of the same type. So depth is counted in `li` ancestors: every
 * nested list in HTML sits inside an `<li>`, which makes an `ol` with N of them above it
 * level N+1. That is type-agnostic, so a bullet list nested between two ordered lists
 * still counts as a level, exactly as it does in Word.
 *
 * Three rules govern every edit here:
 *
 * 1. NEVER put a comma inside parentheses. Griffel 1.21.3 rewrites every comma in a
 *    selector key to `,&` before stylis runs (PSEUDO_SELECTOR_REGEX in
 *    @griffel/core/runtime/compileAtomicCSSRule.js) without looking at parentheses, so
 *    `'& :is(ol,ul) ol'` compiles to `.fXXXX :is(ol,.fXXXXul) ol` — valid CSS, wrong
 *    meaning, no warning. Comma-separated selectors are only safe at the top level with
 *    an explicit `&` on each alternative, as in `'& ol, & ul'`.
 *
 * 2. Every depth selector ends in `:not([type])`. The `type` attribute of `<ol type="a">`
 *    is only a presentational hint and loses to any author rule, so rather than trying to
 *    out-specify it we exclude typed lists from the cascade and let the browser render
 *    them. That keeps the lettered lists of #4735 working for every value (1 → decimal,
 *    a → lower-alpha, A → upper-alpha) and sidesteps the a/A collision an attribute
 *    selector would have had, since HTML matches `type` case-insensitively. A chosen
 *    marker therefore governs its own level only; sublevels carry no `type` and keep
 *    following the cascade.
 *
 * 3. The keys MUST stay ordered shallow → deep. In a browser the cascade is decided by
 *    specificity (each level adds one `li`, so the deepest match always wins), but jsdom
 *    ignores specificity and takes the last matching rule in source order. Ordering the
 *    keys makes both agree, which is what makes the unit tests meaningful. Grouping the
 *    levels by marker breaks jest only.
 */

/**
 * One indent step. Deliberately equal to the 40px/level step of the back office Indent
 * extension, so a list nested N levels deep and a `<p style="margin-left: N*40px">` share
 * a text edge. It is also every browser's default for `padding-inline-start` on a list —
 * pinning it makes the coupling explicit and immune to a future UA change or CSS reset.
 */
const LIST_INDENT = '40px'

export const rtfListStyles: GriffelStyle = {
   '& ol, & ul': {
      paddingInlineStart: LIST_INDENT,
      // The marker sits in the padding, outside the text block, so every item on a level
      // shares one left edge — as in Word. `inside` would reflow wrapped lines under the
      // marker.
      listStylePosition: 'outside',
   },

   '& ol:not([type])': { listStyleType: 'decimal' }, // level 1   1.
   '& ul:not([type])': { listStyleType: 'disc' }, // level 1   •
   '& li ol:not([type])': { listStyleType: 'lower-alpha' }, // level 2   a.
   '& li ul:not([type])': { listStyleType: 'circle' }, // level 2   o
   '& li li ol:not([type])': { listStyleType: 'lower-roman' }, // level 3   i.
   '& li li ul:not([type])': { listStyleType: 'square' }, // level 3   ▪
   '& li li li ol:not([type])': { listStyleType: 'decimal' }, // level 4
   '& li li li ul:not([type])': { listStyleType: 'disc' }, // level 4
   '& li li li li ol:not([type])': { listStyleType: 'lower-alpha' }, // level 5
   '& li li li li ul:not([type])': { listStyleType: 'circle' }, // level 5
   '& li li li li li ol:not([type])': { listStyleType: 'lower-roman' }, // level 6
   '& li li li li li ul:not([type])': { listStyleType: 'square' }, // level 6
   '& li li li li li li ol:not([type])': { listStyleType: 'decimal' }, // level 7
   '& li li li li li li ul:not([type])': { listStyleType: 'disc' }, // level 7
   '& li li li li li li li ol:not([type])': { listStyleType: 'lower-alpha' }, // level 8
   '& li li li li li li li ul:not([type])': { listStyleType: 'circle' }, // level 8
   '& li li li li li li li li ol:not([type])': { listStyleType: 'lower-roman' }, // level 9
   '& li li li li li li li li ul:not([type])': { listStyleType: 'square' }, // level 9
   // Word defines nine levels; anything deeper keeps the level 9 marker.
}
